<template>
    <div class="event-service-list">
        <div class="event-service-list__heading">
            <h1 class="app-h1">
                Список услуг
            </h1>
            <i
                class="fas fa-download event-service-list__download"
                title="Скачать расписание в xlsx"
                @click="_export"
            />
        </div>

        <div class="event-service-list__toolbar">
            <el-input
                v-model.trim="search"
                class="event-service-list__search"
                clearable
                placeholder="Поиск по услугам"
            />

            <el-button
                type="primary"
                @click="_addServiceToEvent"
            >
                Добавить
            </el-button>
        </div>

        <div class="event-service-list__table-scroll">
            <el-table
                v-loading="loading"
                :data="preparedData"
                class="event-service-list__table"
                style="width: 100%"
            >
                <el-table-column
                    :width="400"
                    label="Название"
                >
                    <template slot-scope="scope">
                        <router-link :to="_servicePrintLink(scope.row.serviceId)">
                            {{ scope.row.serviceTitle }}
                        </router-link>
                        <i
                            class="el-icon-edit-outline event-service-list__icon event-service-list__edit"
                            @click="_onEventServiceRowEdit(scope.row)"
                        />
                    </template>
                </el-table-column>
                <el-table-column
                    label="Специалист"
                    prop="specialistName"
                />
                <el-table-column label="Место проведения">
                    <template slot-scope="scope">
                        <div>
                            {{ scope.row.locationName }}
                        </div>
                        <div style="color:#888; font-size: .8em;">
                            {{ scope.row.locationDescription }}
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="Цена">
                    <template slot-scope="scope">
                        <!--eslint-disable-->
                        <div v-html="scope.row.servicePrice" />
                        <!--eslint-enable-->
                    </template>
                </el-table-column>
                <el-table-column
                    label="Группировка"
                >
                    <template slot-scope="scope">
                        <el-tag
                            v-if="scope.row.group"
                            size="mini"
                            effect="dark"
                            style="line-height: 16px;"
                        >
                            {{ scope.row.group }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column
                    prop="participants_registered"
                    :width="70"
                    label="Кол-во"
                />
                <el-table-column
                    :width="60"
                    align="center"
                >
                    <template slot-scope="scope">
                        <i
                            class="el-icon-delete event-service-list__icon event-service-list__remove"
                            @click="_onEventServiceRowRemove(scope)"
                        />
                    </template>
                </el-table-column>
            </el-table>
        </div>
        <event-service-form
            ref="form"
            @added="_onEventServiceAdded"
            @updated="_onEventServiceUpdated"
        />
    </div>
</template>

<script>
import _ from 'lodash';
import money from '@/filters/money';
import { mapActions, mapState, mapMutations } from 'vuex';
import { Event } from '@/api';
import EventServiceForm from '@/modules/event/components/card/service/EventServiceForm';
import ROUTES from '@/enums/routes';

/**
 * @typedef {Object} EventServiceListRow
 * @property {String} locationName
 * @property {String} serviceTitle
 * @property {Number} servicePrice
 * @property {String} specialistName
 * @property {String} locationDescription
 * @property {String} group
 * @property {UUID} serviceId
 * @property {EventServiceResource} resource
 */

export default {
    name: 'EventServiceList',

    components: {
        EventServiceForm
    },

    props: {
        eventId: {
            type: String,
            default: null
        },
        eventTitle: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            loading: false,
            search: ''
        };
    },

    computed: {
        ...mapState('event/service', [
            'services'
        ]),

        /**
         *
         * @returns {EventServiceListRow[]}
         */
        preparedData() {
            /** @param {EventServiceResource} x */
            const mapper = x => {
                /** @type {EventServicePlanResource[]} */
                const plans = _.get(x, 'relationships.plans', []);
                /** @param {EventServicePlanResource} plan */
                const mapper = plan => {
                    return `${plan.attributes.title}: ${money(plan.attributes.default_price, 0)}`;
                };
                const planInfo = plans.map(mapper);
                return {
                    eventServiceId: _.get(x, 'id'),
                    serviceId: _.get(x, 'attributes.service_id'),
                    serviceTitle: _.get(x, 'attributes.service_title'),
                    group: _.get(x, 'attributes.group'),
                    specialistName: _.get(x, 'attributes.specialist_name') || 'Не указан',
                    servicePrice: planInfo.join('<br>') || 'Не указана',
                    locationName: _.get(x, 'attributes.location_title', 'Не указано'),
                    locationDescription: _.get(x, 'attributes.location_description') || '',
                    participants_registered: _.get(x, 'attributes.participants_registered'),
                    resource: x
                };
            };
            const preparedServices = this.services.map(mapper);
            const search = this.search.trim().toLowerCase();

            if (!search) {
                return preparedServices;
            }

            return preparedServices.filter(service => {
                const values = [
                    service.serviceTitle,
                    service.specialistName,
                    service.locationName,
                    service.locationDescription,
                    service.group
                ];

                return values.some(value => (value || '').toLowerCase().includes(search));
            });
        },

        config() {
            return {
                methods: row => ({
                    servicePrintLink: this._servicePrintLink.bind(this, row)
                }),
                columns: [
                    {
                        title: 'Место проведения',
                        render: row => `<div>
                                ${row.locationName}
                            </div>
                            <div style="color:#888; font-size: .8em;">
                                ${row.locationDescription}
                            </div>`
                    },
                    {
                        title: 'Стоимость',
                        render: row => row.servicePrice
                    }
                ]
            };
        }
    },

    mounted() {
        this._loadServices();
    },

    methods: {
        ...mapActions('event/service', [
            'getServices'
        ]),

        ...mapMutations('event/service', [
            'setEventServiceToEdit'
        ]),

        /**
         * @param {{row: EventServiceListRow}} scope
         * @private
         */
        _onEventServiceRowRemove(scope) {
            this._doRemove(scope.row);
        },

        /**
         * @param {EventServiceListRow} row
         */
        _doRemove(row) {
            this.$confirm(
                'Вы хотите удалить услугу вместе с расписанием?',
                'Подтверждение удаления',
                {
                    confirmButtonText: 'Удалить',
                    cancelButtonText: 'Отмена',
                    type: 'warning'
                }
            )
                .then(() => {
                    this.loading = true;
                    return Event.Service.deleteEventService({
                        eventId: this.eventId,
                        eventServiceId: row.eventServiceId
                    });
                })
                .then(() => {
                    this.$message.success('Услуга удалена');
                    return this._loadServices();
                })
                .catch(error => {
                    if (error === 'cancel') {
                        return;
                    }
                    const message = _.get(error, 'message') || 'Не удалось удалить услугу';
                    this.$message.error(message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        /**
         * @param {EventServiceListRow} row
         * @private
         */
        _onEventServiceRowEdit(row) {
            this._doEdit(row);
        },

        /**
         * @param {EventServiceListRow} row
         */
        _doEdit(row) {
            this.setEventServiceToEdit({
                eventServiceToEdit: row.resource
            });
            this.$refs.form.open();
        },

        _loadServices() {
            this.loading = true;
            this.getServices({ eventId: this.eventId })
                .then(() => {
                    this.loading = false;
                })
            ;
        },

        _export() {
            this.loading = true;

            Event.Service.exportFullSchedule({
                eventId: this.eventId
            })
                .then(({ blob, fileName }) => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = fileName || this._buildFallbackExportFileName();
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    this.$message.success('Экспорт завершен');
                })
                .catch(error => {
                    const message = _.get(error, 'message') || 'При экспорте произошла ошибка';
                    this.$message.error(message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        /**
         * @param {UUID} serviceId
         * @returns {{name: string, params: {serviceId: *}}}
         * @private
         */
        _servicePrintLink(serviceId) {
            return {
                name: ROUTES.EVENT.CARD.SERVICE.PARTICIPANTS,
                params: {
                    serviceId
                }
            };
        },

        _onEventServiceAdded() {
            this._loadServices();
        },

        _onEventServiceUpdated() {
            this._loadServices();
        },

        _addServiceToEvent() {
            this.setEventServiceToEdit({
                eventServiceToEdit: null
            });
            this.$refs.form.open();
        },

        _buildFallbackExportFileName() {
            const normalizedTitle = (this.eventTitle || '')
                .trim()
                .replace(/[\\/:*?"<>|]+/g, '_');

            if (normalizedTitle) {
                return `${normalizedTitle}_full_schedule.xlsx`;
            }

            return `Событие_${this.eventId}_full_schedule.xlsx`;
        }
    }
};
</script>

<style lang="scss">
.event-service-list {
  &__heading {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
  }

  &__search {
    width: 100%;
    max-width: 300px;
  }

  &__table-scroll {
    width: 100%;
    overflow-x: auto;
  }

  &__table {
    min-width: 1100px;
  }

  &__icon {
    font-size: 1.2em;
    cursor: pointer;
    visibility: hidden;
  }

  &__download {
    margin-top: -4px;
    color: #409eff;
    font-size: 18px;
    cursor: pointer;
  }

  &__edit {
    color: #0486fe;
    margin-left: 10px;
  }

  &__remove {
    color: #eb2929;
  }

  .el-table__row {
    &:hover {
      .event-service-list__icon {
        visibility: visible;
      }
    }
  }
}

</style>
